import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Loader2, 
  History, 
  User, 
  FileText, 
  DollarSign,
  Building2,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActionBadge = ({ action }) => {
  const colors = {
    create: 'bg-emerald-500',
    edit: 'bg-amber-500',
    update: 'bg-blue-500',
    delete: 'bg-red-500',
    bulk_create: 'bg-violet-500',
    password_reset: 'bg-orange-500'
  };
  
  return (
    <Badge className={colors[action] || 'bg-slate-500'}>
      {action.replace('_', ' ')}
    </Badge>
  );
};

const EntityIcon = ({ type }) => {
  const icons = {
    entry: <FileText className="w-4 h-4 text-blue-500" />,
    budget: <DollarSign className="w-4 h-4 text-emerald-500" />,
    user: <User className="w-4 h-4 text-violet-500" />
  };
  return icons[type] || <FileText className="w-4 h-4" />;
};

export default function AuditLogPage() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  
  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    Promise.all([fetchLogs(), fetchLocations()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [filterType, filterLocation, startDate, endDate]);

  const fetchLogs = async () => {
    try {
      const params = { limit: 200 };
      if (filterType) params.entity_type = filterType;
      if (filterLocation) params.location_id = filterLocation;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const res = await axios.get(`${API}/audit-logs`, { params });
      setLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const toggleExpand = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const formatChanges = (changes) => {
    if (!changes) return null;
    
    // Special handling for entry edits
    if (changes.fields_changed) {
      return (
        <div className="space-y-2">
          {changes.reason && (
            <div className="text-sm">
              <span className="font-medium text-slate-600">Reason: </span>
              <span className="text-slate-800">{changes.reason}</span>
            </div>
          )}
          <div className="space-y-1">
            {Object.entries(changes.fields_changed).map(([field, vals]) => (
              <div key={field} className="text-sm flex items-center gap-2">
                <span className="font-mono text-violet-600">{field}:</span>
                <span className="text-red-500 line-through">{JSON.stringify(vals.old)}</span>
                <span className="text-slate-400">→</span>
                <span className="text-emerald-600">{JSON.stringify(vals.new)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Default rendering
    return (
      <pre className="text-xs bg-slate-50 p-2 rounded overflow-x-auto">
        {JSON.stringify(changes, null, 2)}
      </pre>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="audit-log-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Audit Log
          </h1>
          <p className="text-slate-600 mt-1">
            Track all changes made by executives
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-40">
                <Label className="text-sm text-slate-600 mb-2 block">Entity Type</Label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                >
                  <option value="">All Types</option>
                  <option value="entry">Entries</option>
                  <option value="budget">Budgets</option>
                  <option value="user">Users</option>
                </select>
              </div>
              <div className="flex-1">
                <Label className="text-sm text-slate-600 mb-2 block">Location</Label>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white"
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="w-full md:w-40">
                <Label className="text-sm text-slate-600 mb-2 block">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="w-full md:w-40">
                <Label className="text-sm text-slate-600 mb-2 block">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-violet-600" />
              Activity History ({logs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No audit logs found
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map(log => (
                  <div 
                    key={log.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    <div 
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => toggleExpand(log.id)}
                    >
                      <div className="flex items-center gap-4">
                        <EntityIcon type={log.entity_type} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium capitalize">{log.entity_type}</span>
                            <ActionBadge action={log.action} />
                          </div>
                          <div className="text-sm text-slate-500">
                            by {log.performed_by_name} • {new Date(log.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {log.location_name && (
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Building2 className="w-3 h-3" />
                            {log.location_name}
                          </div>
                        )}
                        {expandedLogs.has(log.id) ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </div>
                    
                    {expandedLogs.has(log.id) && (
                      <div className="p-4 border-t bg-white">
                        <div className="text-sm text-slate-600 mb-2">Changes:</div>
                        {formatChanges(log.changes)}
                        <div className="mt-3 text-xs text-slate-400">
                          Entity ID: {log.entity_id}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
